import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import MentionTextarea from "./MentionTextarea";
import { useTeamMembers } from "../hooks/useTeamMembers";
import { getGraphClient } from "../services/graphClient";
import { getThumbnailUrl } from "../services/sharepoint";

function initials(name) {
  if (!name) return "?";
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " at " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

// Automated audit entries are stored with this marker so they can be
// told apart from real user comments.
const SYSTEM_PREFIX = "[status]";
function isSystemComment(text) {
  return typeof text === "string" && text.trim().startsWith(SYSTEM_PREFIX);
}
function stripSystemPrefix(text) {
  return text.trim().slice(SYSTEM_PREFIX.length).trim();
}
function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function CommentText({ text }) {
  const parts = text.split(/(@[A-Za-z]+(?:\s[A-Za-z]+)*)/g);
  return (
    <div className="c-text">
      {parts.map((part, i) =>
        part.startsWith("@") ? (
          <span key={i} className="mention-highlight">{part}</span>
        ) : (
          part
        )
      )}
    </div>
  );
}

const STATUSES = ["Ordered", "Partially Received", "Received", "Closed / Invoiced"];

function StatusPill({ status, onStatusChange }) {
  const [open, setOpen] = React.useState(false);
  // Track the displayed status locally so it updates instantly on click,
  // and stays in sync if the underlying prop changes (e.g. panel reopened).
  const [localStatus, setLocalStatus] = React.useState(status);
  React.useEffect(() => { setLocalStatus(status); }, [status]);

  const map = {
    "Ordered":             { cls: "pill-ordered",  label: "Ordered"             },
    "Partially Received":  { cls: "pill-partial",  label: "Partially Received"  },
    "Received":            { cls: "pill-received", label: "Received"            },
    "Closed / Invoiced":   { cls: "pill-closed",   label: "Closed / Invoiced"   },
  };
  const current = map[localStatus] || map["Ordered"];

  const choose = (s) => {
    setLocalStatus(s);          // update the pill immediately
    setOpen(false);
    onStatusChange && onStatusChange(s);  // save in the background
  };

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <span
        className={`pill ${current.cls}`}
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((o) => !o)}
        title="Click to change status"
      >
        {current.label}
        <i className="ti ti-chevron-down" style={{ fontSize: 11, marginLeft: 4, verticalAlign: -1 }} aria-hidden="true" />
      </span>
      {open && (
        <div className="status-dropdown">
          {STATUSES.map((s) => (
            <div
              key={s}
              className={`status-option${s === localStatus ? " active" : ""}`}
              onClick={() => choose(s)}
            >
              <span className={`pill ${map[s]?.cls || "pill-ordered"}`}>{s}</span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

function PDFPreview({ po, driveId }) {
  const { instance, accounts } = useMsal();
  const [thumbUrl, setThumbUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!driveId || !po.id) return;
    let cancelled = false;
    setLoading(true);
    setThumbUrl(null);
    (async () => {
      try {
        const client = getGraphClient(instance, accounts[0]);
        const res = await client
          .api(`/drives/${driveId}/items/${po.id}/thumbnails/0/large`)
          .get();
        if (!cancelled) setThumbUrl(res.url || null);
      } catch {
        if (!cancelled) setThumbUrl(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [po.id, driveId, instance, accounts]);

  if (loading) {
    return <div className="preview-fallback"><div className="spinner" /></div>;
  }

  if (thumbUrl) {
    return (
      <div className="preview-thumb-wrap">
        <img src={thumbUrl} alt={`Preview of ${po.name}`} className="preview-thumb-img" />
      </div>
    );
  }

  return (
    <div className="preview-fallback">
      <i className="ti ti-file-type-pdf" style={{ fontSize: 44, color: "var(--pdf-red)", marginBottom: 10 }} aria-hidden="true" />
      <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 12 }}>{po.name}</p>
      <a href={po.webUrl} target="_blank" rel="noreferrer" className="btn btn-primary">
        <i className="ti ti-external-link" style={{ fontSize: 15 }} aria-hidden="true" />
        Open PDF in SharePoint
      </a>
    </div>
  );
}

export default function DetailPanel({ po, comments, loadingComments, onPostComment, onStatusChange, onClose, driveId }) {
  const { instance, accounts } = useMsal();
  const [text, setText] = useState("");
  const [mentionedEmails, setMentionedEmails] = useState([]);
  const [posting, setPosting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const members = useTeamMembers();

  const handlePost = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await onPostComment(text.trim(), mentionedEmails);
      setText("");
      setMentionedEmails([]);
    } finally {
      setPosting(false);
    }
  };

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const client = getGraphClient(instance, accounts[0]);
      const item = await client
        .api(`/drives/${driveId}/items/${po.id}`)
        .select("@microsoft.graph.downloadUrl")
        .get();
      const url = item["@microsoft.graph.downloadUrl"];
      if (!url) {
        window.open(po.webUrl, "_blank");
        return;
      }
      const resp = await fetch(url);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.src = blobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch {
            window.open(po.webUrl, "_blank");
          }
        }, 300);
      };
    } catch (e) {
      console.error("[print] error:", e);
      window.open(po.webUrl, "_blank");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>{po.name}</h2>
        <button className="icon-btn" onClick={onClose} aria-label="Close panel">
          <i className="ti ti-x" aria-hidden="true" />
        </button>
      </div>

      <div className="panel-preview">
        <PDFPreview po={po} driveId={driveId} />
      </div>

      <div className="panel-body">
        <div className="detail-field">
          <div className="detail-label">Status</div>
          <div className="detail-value">
            <StatusPill status={po.fields?.Status || "Ordered"} onStatusChange={onStatusChange} />
          </div>
        </div>

        <div className="panel-actions-row">
          <a href={po.webUrl} target="_blank" rel="noreferrer" className="panel-action-btn">
            <i className="ti ti-external-link" style={{ fontSize: 15 }} aria-hidden="true" />
            Open PDF
          </a>
          <button
            className="panel-action-btn"
            onClick={handlePrint}
            disabled={printing}
            style={{ cursor: "pointer", font: "inherit" }}
          >
            <i className={`ti ${printing ? "ti-loader-2 ti-spin" : "ti-printer"}`} style={{ fontSize: 15 }} aria-hidden="true" />
            {printing ? "Preparing…" : "Print"}
          </button>
        </div>

        <div className="section-label">
          Comments ({loadingComments ? "…" : comments.length})
        </div>

        {loadingComments ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "16px 0" }}>
            <div className="spinner" />
          </div>
        ) : (
          <div className="comment-list">
            {comments.length === 0 && (
              <p className="no-comments">No comments yet. Use @ to mention a teammate.</p>
            )}
            {comments.map((c) =>
              isSystemComment(c.text) ? (
                <div key={c.id} className="comment-card system-comment">
                  <div className="system-line">
                    <i className="ti ti-settings system-icon" aria-hidden="true" />
                    <span>{stripSystemPrefix(c.text)} by {c.author}</span>
                    <span className="system-time">{fmtDateTime(c.created)}</span>
                  </div>
                </div>
              ) : (
                <div key={c.id} className="comment-card">
                  <div className="comment-meta">
                    <div className="c-avatar">{initials(c.author)}</div>
                    <span className="c-author">{c.author}</span>
                    <span className="c-time">{fmtDate(c.created)}</span>
                  </div>
                  <CommentText text={c.text} />
                </div>
              )
            )}
          </div>
        )}
      </div>

      <div className="comment-form">
        <MentionTextarea
          value={text}
          onChange={setText}
          onMentionsChange={setMentionedEmails}
          onSubmit={handlePost}
          members={members}
          placeholder={`Comment on ${po.name}… type @ to mention someone`}
          rows={3}
        />
        {mentionedEmails.length > 0 && (
          <p className="notify-note">
            <i className="ti ti-bell" style={{ fontSize: 13, verticalAlign: -2 }} aria-hidden="true" />
            {" "}{mentionedEmails.length} person{mentionedEmails.length > 1 ? "s" : ""} will be notified by email
          </p>
        )}
        <button
          className="btn btn-primary"
          style={{ alignSelf: "flex-end" }}
          disabled={!text.trim() || posting}
          onClick={handlePost}
        >
          {posting ? "Posting…" : "Post comment"}
        </button>
      </div>
    </div>
  );
}
